import { CatalogFlow, normalizeCatalogNode } from './catalogDoc';
import { GraphDevice } from './graph';
import {
  CatalogWizardArgs,
  asGroup,
  ensureBus,
  ensureController,
  ensureDashboard,
  ensureDevice,
  ensurePoint,
  finishChain,
  link,
  startChain,
  upsertDevice,
  wireToMqtt,
} from './catalogChain';

const SML_POINTS = ['energy', 'power', 'energy_reverse'] as const;
const DASH_PORTS: Record<(typeof SML_POINTS)[number], string> = {
  energy: 'in',
  power: 'power',
  energy_reverse: 'reverse',
};

/** Dual-write serial.mode sml and the meter instance. UART pins stay in settings. */
export function applySmlHardware(
  values: Record<string, unknown>,
  device: GraphDevice,
): Record<string, unknown> {
  const connection = String(device.connection || 'uart').trim() || 'uart';
  const serial = asGroup(values.serial);
  const instance: GraphDevice = {
    ...device,
    id: String(device.id || 'sml').trim() || 'sml',
    type: 'sml',
    enable: device.enable !== false,
    connection,
  };
  return {
    ...values,
    serial: {
      ...serial,
      mode: 'sml',
    },
    mqtt: { ...asGroup(values.mqtt), enable: true },
    devices: upsertDevice(values, instance),
  };
}

export function mergeSmlCatalog(doc: CatalogFlow, args: CatalogWizardArgs): CatalogFlow {
  const state = startChain(doc);
  const of = String(args.device.id || 'sml').trim() || 'sml';
  const connection = String(args.device.connection || 'uart').trim() || 'uart';
  const controller = ensureController(state, args);
  const bus = ensureBus(state, {
    of: connection,
    busType: 'uart',
    x: state.x0 + 160,
    y: state.y0,
  });
  const device = ensureDevice(state, {
    of,
    deviceType: 'sml',
    x: state.x0 + 320,
    y: state.y0,
  });
  link(state, `${controller.id}.host`, `${bus.id}.host`);
  link(state, `${bus.id}.read`, `${device.id}.read`);
  const mqttByKey: Record<string, { id: string }> = {};
  SML_POINTS.forEach((key, index) => {
    const point = ensurePoint(state, {
      of,
      key,
      poll: false,
      x: state.x0 + 500,
      y: state.y0 + index * 140,
      deviceId: device.id,
    });
    mqttByKey[key] = wireToMqtt(state, {
      point,
      topic: `{base}/${of}/${key}`,
      y: state.y0 + index * 140,
      xCalc: state.x0 + 680,
      xMqtt: state.x0 + 680,
      from: 'event',
    });
  });
  if (args.widget === 'sml_meter') {
    const dash = ensureDashboard(state, {
      widgetType: 'sml_meter',
      title: args.hostname ? `${args.hostname} · ${of}` : of,
      x: state.x0 + 900,
      y: state.y0 + 140,
    });
    SML_POINTS.forEach((key) => {
      link(state, `${mqttByKey[key].id}.subscribe`, `${dash.id}.${DASH_PORTS[key]}`);
    });
  }
  const next = finishChain(doc, args, state);
  return { ...next, nodes: next.nodes.map(normalizeCatalogNode) };
}
